import {
  createContext,
  type FC,
  type ReactElement,
  useContext,
  useMemo,
  useState,
} from 'react'

import { type OpenableControls } from '../../generic-hooks'
import { type PersonalAdviceProduct } from '../PersonalAdviceProduct'

type PersonalAdviceDialogWithoutAccordionContextValue = {
  dialogControls: OpenableControls
  product?: PersonalAdviceProduct
  selectedContent: ReactElement | null
  setSelectedContent: (content: ReactElement) => void
}

const PersonalAdviceDialogWithoutAccordionContext = createContext<
  PersonalAdviceDialogWithoutAccordionContextValue | undefined
>(undefined)

export const PersonalAdviceDialogWithoutAccordionProvider: FC<{
  children: ReactElement
  dialogControls: OpenableControls
  product?: PersonalAdviceProduct
}> = ({ children, dialogControls, product }) => {
  const [selectedContent, setSelectedContent] = useState<ReactElement | null>(
    null,
  )

  const memoizedValue: PersonalAdviceDialogWithoutAccordionContextValue =
    useMemo(
      () => ({
        dialogControls: {
          ...dialogControls,
          handleClose: (): void => {
            setSelectedContent(null)
            dialogControls.handleClose()
          },
        },
        product,
        selectedContent,
        setSelectedContent,
      }),
      [dialogControls, product, selectedContent],
    )

  return (
    <PersonalAdviceDialogWithoutAccordionContext.Provider value={memoizedValue}>
      {children}
    </PersonalAdviceDialogWithoutAccordionContext.Provider>
  )
}

export const usePersonalAdviceDialogWithoutAccordionContext =
  (): PersonalAdviceDialogWithoutAccordionContextValue => {
    const context = useContext(PersonalAdviceDialogWithoutAccordionContext)
    if (context === undefined) {
      throw new Error(
        'usePersonalAdviceDialogWithoutAccordionContext must be used within a PersonalAdviceDialogWithoutAccordionProvider',
      )
    }

    return context
  }
